import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import api from '@/lib/api'
import { AlertCircle, Check, Lock, User } from 'lucide-react'

const ROLE_DISPLAY: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  director: 'Director',
  sales_manager: 'Sales Manager',
  asst_sales_manager: 'Asst Sales Manager',
  sales_executive: 'Sales Executive',
  chief_engineer: 'Chief Engineer',
  operation_manager: 'Operation Manager',
  engineer: 'Engineer',
  technician: 'Technician',
  finance: 'Finance',
  procurement: 'Procurement',
  storekeeper: 'Storekeeper',
  hr: 'HR',
}

export default function Profile() {
  const { user } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const changePassword = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      api.post('/users/change-password', data),
    onSuccess: () => {
      setSuccess('Password changed successfully')
      setError('')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to change password')
      setSuccess('')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }
    changePassword.mutate({ current_password: currentPassword, new_password: newPassword })
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900">My Profile</h1>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <User size={24} className="text-red-800" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">{user?.full_name}</div>
            <div className="text-sm text-slate-500">@{user?.username}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Role</span>
            <div className="font-medium text-slate-900">{ROLE_DISPLAY[user?.role || ''] || user?.role}</div>
          </div>
          <div>
            <span className="text-slate-500">Email</span>
            <div className="font-medium text-slate-900">{user?.email || '—'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={18} className="text-slate-700" />
          <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200 mb-4">
            <AlertCircle size={16} className="shrink-0" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm px-4 py-3 rounded-lg border border-green-200 mb-4">
            <Check size={16} className="shrink-0" /><span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              required
              minLength={6}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={changePassword.isPending}
            className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 font-medium"
          >
            {changePassword.isPending ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
